import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const normalizeUser = (userData) => {
  if (!userData) return null;
  const roles = Array.isArray(userData.roles)
    ? userData.roles
    : userData.role
      ? [userData.role]
      : [];
  return { ...userData, roles };
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      if (authService.isAuthenticated()) {
        const storedUser = normalizeUser(authService.getCurrentUser());
        if (storedUser) {
          setUser(storedUser);
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    try {
      const result = await authService.login(username, password);

      if (result.success) {
        const nextUser = normalizeUser(result.user);
        setUser(nextUser);
        setIsAuthenticated(true);
        return { success: true, user: nextUser };
      }

      return {
        success: false,
        error: result.error || 'Invalid username or password'
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const updateUserProfile = (userData) => {
    setUser(prevUser => {
      const updatedUser = normalizeUser({ ...prevUser, ...userData });
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || user?.role === role || false;
  };

  const hasAnyRole = (roles) => {
    return roles.some(role => user?.roles?.includes(role) || user?.role === role) || false;
  };

  const getUserPermissions = () => user?.permissions || {};

  const getToken = () => localStorage.getItem('access_token');

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    updateUserProfile,
    hasRole,
    hasAnyRole,
    getUserPermissions,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
